import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaTelegramPlane } from 'react-icons/fa';
import { FiCalendar, FiCopy, FiSend, FiX } from 'react-icons/fi';
import { formatSingaporeDateTime, singaporeNowDatetimeLocal, toSingaporeDatetimeLocalValue } from '../utils/singaporeTime';

const API_BASE = process.env.REACT_APP_API_URL || '';

function getPhotoDisplayUrl(memberId, folder, filename) {
    if (!memberId || !folder || !filename) return '';
    const base = API_BASE.replace(/\/$/, '');
    return `${base}/api/godmembers/${memberId}/media/${folder}/${encodeURIComponent(filename)}`;
}

const defaultScheduleValue = () => {
    const oneHourLater = new Date(Date.now() + 60 * 60 * 1000);
    return toSingaporeDatetimeLocalValue(oneHourLater);
};

const CAPTION_LIMIT = 1024;
const CAPTION_WARN_OVER = 1020;

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        try {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            return true;
        } catch {
            return false;
        }
    }
};

const ReviewSendTelegramModal = ({ reviewId, onClose, onSuccess, getAuthHeaders }) => {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [scheduling, setScheduling] = useState(false);
    const [showSchedule, setShowSchedule] = useState(false);
    const [scheduledAt, setScheduledAt] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [cancelling, setCancelling] = useState(false);
    const [captionCopied, setCaptionCopied] = useState(false);
    const [lastPostDateInput, setLastPostDateInput] = useState('');
    const [savingLastPost, setSavingLastPost] = useState(false);

    const loadPreview = async () => {
        const res = await axios.get(
            `${API_BASE}/api/godreviews/${reviewId}/telegram-preview`,
            { headers: getAuthHeaders() }
        );
        if (res.data.success) setPreview(res.data.data);
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                setError('');
                const res = await axios.get(
                    `${API_BASE}/api/godreviews/${reviewId}/telegram-preview`,
                    { headers: getAuthHeaders() }
                );
                if (!cancelled && res.data.success) setPreview(res.data.data);
            } catch (err) {
                if (!cancelled) {
                    setError(err.response?.data?.error || err.message || 'Failed to load preview');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [reviewId]);

    useEffect(() => {
        setCaptionCopied(false);
    }, [reviewId]);

    useEffect(() => {
        if (!captionCopied) return undefined;
        const t = setTimeout(() => setCaptionCopied(false), 2000);
        return () => clearTimeout(t);
    }, [captionCopied]);

    useEffect(() => {
        if (!preview) return;
        setLastPostDateInput(
            toSingaporeDatetimeLocalValue(preview.lastPostDate) || singaporeNowDatetimeLocal()
        );
    }, [preview]);

    const openSchedule = () => {
        setShowSchedule(true);
        setSuccess('');
        setError('');
        if (!scheduledAt) setScheduledAt(defaultScheduleValue());
    };

    const handleSend = async () => {
        try {
            setSending(true);
            setError('');
            setSuccess('');
            await axios.post(
                `${API_BASE}/api/godreviews/${reviewId}/send-telegram`,
                { photos: preview?.photos || [] },
                { headers: getAuthHeaders() }
            );
            onSuccess?.();
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to send');
        } finally {
            setSending(false);
        }
    };

    const handleSchedule = async () => {
        if (!scheduledAt) {
            setError('Please choose a schedule date and time (SG).');
            return;
        }
        try {
            setScheduling(true);
            setError('');
            setSuccess('');
            const res = await axios.post(
                `${API_BASE}/api/godreviews/${reviewId}/schedule-telegram`,
                { photos: preview?.photos || [], scheduledAt },
                { headers: getAuthHeaders() }
            );
            if (res.data.success) {
                setSuccess(
                    `Scheduled for ${formatSingaporeDateTime(res.data.data?.scheduledAt || scheduledAt)} (SG)`
                );
                onSuccess?.();
                setTimeout(() => onClose(), 1200);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to schedule');
        } finally {
            setScheduling(false);
        }
    };

    const handleCancelSchedule = async () => {
        if (!window.confirm('Cancel the pending scheduled Telegram send?')) return;
        try {
            setCancelling(true);
            setError('');
            setSuccess('');
            await axios.delete(
                `${API_BASE}/api/godreviews/${reviewId}/schedule-telegram`,
                { headers: getAuthHeaders() }
            );
            setSuccess('Scheduled send cancelled');
            await loadPreview();
            onSuccess?.();
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to cancel schedule');
        } finally {
            setCancelling(false);
        }
    };

    const handleSaveLastPostDate = async () => {
        if (!lastPostDateInput) {
            setError('Please choose a last post date (SG).');
            return;
        }
        try {
            setSavingLastPost(true);
            setError('');
            setSuccess('');
            await axios.put(
                `${API_BASE}/api/godreviews/${reviewId}`,
                { lastPostDate: lastPostDateInput },
                { headers: getAuthHeaders() }
            );
            setPreview((prev) => (prev ? { ...prev, lastPostDate: lastPostDateInput } : prev));
            setSuccess('Last post date saved');
            onSuccess?.();
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to save last post date');
        } finally {
            setSavingLastPost(false);
        }
    };

    const actionsDisabled = loading || sending || scheduling || cancelling || savingLastPost || !preview || Boolean(error && !preview);

    const captionLength = preview?.captionLength ?? preview?.caption?.length ?? 0;
    const captionExceedsLimit = captionLength > CAPTION_WARN_OVER;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <FaTelegramPlane className="text-sky-500" />
                        Send review to Telegram
                    </h2>
                    <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
                        <FiX size={20} />
                    </button>
                </div>

                <div className="p-4 overflow-y-auto flex-1 space-y-4">
                    {error && (
                        <div className="p-3 bg-red-50 text-red-700 rounded-md text-sm">{error}</div>
                    )}
                    {success && (
                        <div className="p-3 bg-green-50 text-green-700 rounded-md text-sm">{success}</div>
                    )}

                    {loading ? (
                        <p className="text-gray-500 text-center py-8">Loading preview...</p>
                    ) : preview ? (
                        <>
                            {preview.pendingScheduledSend && (
                                <div className="flex flex-wrap items-center justify-between gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                                    <p className="text-sm text-amber-900">
                                        Pending send at {formatSingaporeDateTime(preview.pendingScheduledSend.scheduledAt)} (SG)
                                    </p>
                                    <button
                                        type="button"
                                        onClick={handleCancelSchedule}
                                        disabled={cancelling}
                                        className="px-3 py-1.5 text-sm border border-amber-500 text-amber-700 rounded-md hover:bg-amber-100 disabled:opacity-50"
                                    >
                                        {cancelling ? 'Cancelling...' : 'Cancel schedule'}
                                    </button>
                                </div>
                            )}
                            <div className={`grid gap-3 ${preview.photos?.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                                {(preview.photos || []).map((photo) => (
                                    <img
                                        key={`${photo.mediaType}-${photo.filename}`}
                                        src={getPhotoDisplayUrl(preview.memberId, photo.folder, photo.filename)}
                                        alt={photo.filename}
                                        className="w-full h-80 object-cover rounded-md border border-gray-200 bg-gray-50"
                                    />
                                ))}
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <p className="text-xs font-medium text-gray-500">Caption preview</p>
                                    {captionExceedsLimit && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    const text = preview.caption || '';
                                                    if (!text) return;
                                                    const ok = await copyToClipboard(text);
                                                    if (ok) setCaptionCopied(true);
                                                }}
                                                className="px-1 py-0.5 text-xs bg-gray-100 rounded hover:bg-gray-200 shrink-0"
                                                title="Copy caption"
                                            >
                                                <FiCopy className="w-3.5 h-3.5" />
                                            </button>
                                            {captionCopied && (
                                                <span className="text-xs font-medium text-green-600 shrink-0">Copied</span>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div
                                    className="whitespace-pre-wrap text-sm text-gray-800 border border-gray-200 rounded-md p-3 bg-gray-50 font-sans [&_a]:text-blue-600 [&_a]:underline"
                                    dangerouslySetInnerHTML={{
                                        __html: String(preview.caption || '').replace(/\n/g, '<br/>'),
                                    }}
                                />
                            </div>
                            {captionExceedsLimit && (
                                <>
                                    <p className="text-sm font-bold text-red-600">
                                        Exceed Caption 1024 Limit Character: {captionLength - CAPTION_LIMIT} ({captionLength})
                                    </p>
                                    <div className="flex flex-wrap items-end gap-2">
                                        <div className="flex-1 min-w-[180px]">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Last Post Date</label>
                                            <input
                                                type="datetime-local"
                                                value={lastPostDateInput}
                                                onChange={(e) => setLastPostDateInput(e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleSaveLastPostDate}
                                            disabled={savingLastPost || sending || scheduling || cancelling || !lastPostDateInput}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
                                        >
                                            {savingLastPost ? 'Saving...' : 'Save'}
                                        </button>
                                    </div>
                                </>
                            )}
                            <p className="text-xs text-gray-500">
                                Will post to channel ID: <span className="font-mono">{preview.channelId}</span>
                            </p>

                            {showSchedule && (
                                <div className="border border-amber-200 bg-amber-50 rounded-md p-4 space-y-3">
                                    <p className="text-sm font-medium text-amber-900">Schedule send (Singapore time)</p>
                                    <input
                                        type="datetime-local"
                                        value={scheduledAt}
                                        onChange={(e) => setScheduledAt(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    />
                                    <p className="text-xs text-amber-800">
                                        Caption and photos are saved now. The bot sends automatically at this time.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={handleSchedule}
                                        disabled={actionsDisabled}
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 text-sm"
                                    >
                                        <FiCalendar className="w-4 h-4" />
                                        {scheduling ? 'Scheduling...' : 'Confirm schedule'}
                                    </button>
                                </div>
                            )}
                        </>
                    ) : null}
                </div>

                <div className="p-4 border-t flex flex-wrap justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                        disabled={sending || scheduling || cancelling || savingLastPost}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={openSchedule}
                        disabled={actionsDisabled}
                        className="inline-flex items-center gap-2 px-4 py-2 border border-amber-500 text-amber-700 rounded-md hover:bg-amber-50 disabled:opacity-50"
                    >
                        <FiCalendar className="w-4 h-4" />
                        Schedule
                    </button>
                    <button
                        type="button"
                        onClick={handleSend}
                        disabled={actionsDisabled}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-md hover:bg-sky-700 disabled:opacity-50"
                    >
                        <FiSend className="w-4 h-4" />
                        {sending ? 'Sending...' : 'Send now'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReviewSendTelegramModal;
